import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';

type Kind = 'success' | 'error' | 'info';
export type Toast = { id: number; kind: Kind; message: string };

type Ctx = {
  toasts: Toast[];
  push: (kind: Kind, message: string) => void;
  dismiss: (id: number) => void;
};

const ToastCtx = createContext<Ctx | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(
    (kind: Kind, message: string) => {
      const id = nextId.current++;
      setToasts((prev) => [...prev, { id, kind, message }]);
      // Los mensajes de error suelen ser largos (traducciones de Flexxus) y
      // necesitan más tiempo para leerse; los otros mantienen 5 s.
      const ttl = kind === 'error' ? 9000 : 5000;
      setTimeout(() => dismiss(id), ttl);
    },
    [dismiss],
  );

  return (
    <ToastCtx.Provider value={{ toasts, push, dismiss }}>
      {children}
      <ToastViewport />
    </ToastCtx.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useToast() {
  const ctx = useContext(ToastCtx);
  if (!ctx) throw new Error('useToast fuera de ToastProvider');
  return ctx;
}

const ICON: Record<Kind, string> = {
  success: 'ph-check-circle',
  error: 'ph-warning-circle',
  info: 'ph-info',
};
const COLOR: Record<Kind, string> = {
  success: 'bg-emerald-600 text-white',
  error: 'bg-red-600 text-white',
  info: 'bg-slate-800 text-white',
};

function ToastViewport() {
  const { toasts, dismiss } = useToast();
  return (
    <div className="pointer-events-none fixed bottom-4 right-4 z-50 flex flex-col gap-2">
      {toasts.map((t) => (
        <ToastItem key={t.id} toast={t} onClose={() => dismiss(t.id)} />
      ))}
    </div>
  );
}

function ToastItem({ toast, onClose }: { toast: Toast; onClose: () => void }) {
  const [entering, setEntering] = useState(true);
  useEffect(() => {
    const id = requestAnimationFrame(() => setEntering(false));
    return () => cancelAnimationFrame(id);
  }, []);
  return (
    <div
      className={`pointer-events-auto flex max-w-md items-start gap-2 rounded-md px-3 py-2 text-sm shadow-lg transition ${
        COLOR[toast.kind]
      } ${entering ? 'translate-y-2 opacity-0' : 'translate-y-0 opacity-100'}`}
    >
      <i className={`ph ${ICON[toast.kind]} mt-0.5 flex-none`} />
      <span className="flex-1 break-words">{toast.message}</span>
      <button
        onClick={onClose}
        className="ml-1 rounded-md p-0.5 opacity-70 hover:bg-white/10 hover:opacity-100"
        title="Cerrar"
      >
        <i className="ph ph-x text-xs" />
      </button>
    </div>
  );
}
